import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface ZoomState {
  scale: number;
  tx: number;
  ty: number;
}

const MIN_SCALE = 0.1;
const MAX_SCALE = 20;

export interface ViewState {
  x: number; // SVG center X
  y: number; // SVG center Y
  z: number; // zoom scale
}

export function useFloorplanZoom(viewBox?: string, onViewChange?: (view: ViewState) => void) {
  const vbParts = useMemo(
    () => (viewBox ? viewBox.split(/\s+/).map(Number) : [0, 0, 0, 0]),
    [viewBox],
  );
  const vbOffsetX = vbParts[0] || 0;
  const vbOffsetY = vbParts[1] || 0;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const wheelListenerRef = useRef<((e: WheelEvent) => void) | null>(null);

  // Live state — updated during drag without React re-renders
  const live = useRef<ZoomState>({ scale: 0.5, tx: 0, ty: 0 });
  const [state, setState] = useState<ZoomState>(live.current);
  const hasAutoFit = useRef(false);

  const dragging = useRef(false);
  const didDrag = useRef(false); // true if mouse moved during drag (distinguish click vs pan)
  const lastPos = useRef({ x: 0, y: 0 });

  // Touch state
  const touchState = useRef<{
    active: boolean;
    lastTouches: { x: number; y: number }[];
    lastDist: number;
    lastCenter: { x: number; y: number };
  }>({
    active: false,
    lastTouches: [],
    lastDist: 0,
    lastCenter: { x: 0, y: 0 },
  });

  const transform = `translate(${state.tx}px, ${state.ty}px) scale(${state.scale})`;

  const applyTransform = useCallback(() => {
    if (innerRef.current) {
      innerRef.current.style.transform = `translate(${live.current.tx}px, ${live.current.ty}px) scale(${live.current.scale})`;
    }
  }, []);

  // Sync live state to React state and notify view change
  const onViewChangeRef = useRef(onViewChange);
  onViewChangeRef.current = onViewChange;

  const syncState = useCallback(() => {
    setState({ ...live.current });
    if (onViewChangeRef.current && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const s = live.current;
      const cx = (rect.width / 2 - s.tx) / s.scale + vbOffsetX;
      const cy = (rect.height / 2 - s.ty) / s.scale + vbOffsetY;
      onViewChangeRef.current({
        x: Math.round(cx),
        y: Math.round(cy),
        z: Math.round(s.scale * 100) / 100,
      });
    }
  }, [vbOffsetX, vbOffsetY]);

  // Auto-fit to container on first render
  const autoFit = useCallback(() => {
    const container = containerRef.current;
    if (!container || hasAutoFit.current) return;

    const svgW = vbParts[2] || 0;
    const svgH = vbParts[3] || 0;
    if (!svgW || !svgH) return;

    const rect = container.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const scaleX = rect.width / svgW;
    const scaleY = rect.height / svgH;
    const fitScale = Math.min(scaleX, scaleY) * 0.95;
    const clampedScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, fitScale));

    // Center the floorplan
    const scaledW = svgW * clampedScale;
    const scaledH = svgH * clampedScale;
    const tx = (rect.width - scaledW) / 2;
    const ty = (rect.height - scaledH) / 2;

    live.current = { scale: clampedScale, tx, ty };
    applyTransform();
    syncState();
    hasAutoFit.current = true;
  }, [vbParts, applyTransform, syncState]);

  // Callback ref for the container
  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (containerRef.current && wheelListenerRef.current) {
        containerRef.current.removeEventListener("wheel", wheelListenerRef.current);
      }
      containerRef.current = node;
      if (node) {
        innerRef.current = node.firstElementChild as HTMLDivElement | null;

        // Non-passive wheel handler for zoom
        const handleWheel = (e: WheelEvent) => {
          e.preventDefault();
          const rect = node.getBoundingClientRect();
          const cursorX = e.clientX - rect.left;
          const cursorY = e.clientY - rect.top;
          const prev = live.current;
          const zoomFactor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
          const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev.scale * zoomFactor));
          const ratio = newScale / prev.scale;
          live.current = {
            scale: newScale,
            tx: cursorX - ratio * (cursorX - prev.tx),
            ty: cursorY - ratio * (cursorY - prev.ty),
          };
          applyTransform();
          syncState();
        };

        wheelListenerRef.current = handleWheel;
        node.addEventListener("wheel", handleWheel, { passive: false });

        // Touch handlers (non-passive for preventDefault)
        const handleTouchStart = (e: TouchEvent) => {
          if (e.touches.length === 1) {
            // Single finger pan
            dragging.current = true;
            lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            touchState.current.active = true;
          } else if (e.touches.length === 2) {
            // Pinch zoom
            e.preventDefault();
            dragging.current = false;
            const t0 = e.touches[0];
            const t1 = e.touches[1];
            const dist = Math.sqrt(
              (t1.clientX - t0.clientX) ** 2 + (t1.clientY - t0.clientY) ** 2,
            );
            touchState.current = {
              active: true,
              lastTouches: [
                { x: t0.clientX, y: t0.clientY },
                { x: t1.clientX, y: t1.clientY },
              ],
              lastDist: dist,
              lastCenter: {
                x: (t0.clientX + t1.clientX) / 2,
                y: (t0.clientY + t1.clientY) / 2,
              },
            };
          }
        };

        const handleTouchMove = (e: TouchEvent) => {
          if (e.touches.length === 1 && dragging.current) {
            e.preventDefault();
            const dx = e.touches[0].clientX - lastPos.current.x;
            const dy = e.touches[0].clientY - lastPos.current.y;
            lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            live.current.tx += dx;
            live.current.ty += dy;
            applyTransform();
          } else if (e.touches.length === 2 && touchState.current.active) {
            e.preventDefault();
            const t0 = e.touches[0];
            const t1 = e.touches[1];
            const dist = Math.sqrt(
              (t1.clientX - t0.clientX) ** 2 + (t1.clientY - t0.clientY) ** 2,
            );
            const center = {
              x: (t0.clientX + t1.clientX) / 2,
              y: (t0.clientY + t1.clientY) / 2,
            };

            const rect = node.getBoundingClientRect();
            const prev = live.current;

            // Zoom
            const zoomFactor = dist / touchState.current.lastDist;
            const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev.scale * zoomFactor));
            const ratio = newScale / prev.scale;

            // Zoom toward pinch center
            const cx = center.x - rect.left;
            const cy = center.y - rect.top;

            // Pan by center movement
            const dcx = center.x - touchState.current.lastCenter.x;
            const dcy = center.y - touchState.current.lastCenter.y;

            live.current = {
              scale: newScale,
              tx: cx - ratio * (cx - prev.tx) + dcx,
              ty: cy - ratio * (cy - prev.ty) + dcy,
            };

            touchState.current.lastDist = dist;
            touchState.current.lastCenter = center;
            applyTransform();
          }
        };

        const handleTouchEnd = (e: TouchEvent) => {
          if (e.touches.length === 0) {
            dragging.current = false;
            touchState.current.active = false;
            syncState();
          } else if (e.touches.length === 1) {
            // Went from 2 fingers to 1 — switch to pan
            dragging.current = true;
            lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            touchState.current.active = false;
            syncState();
          }
        };

        node.addEventListener("touchstart", handleTouchStart, { passive: false });
        node.addEventListener("touchmove", handleTouchMove, { passive: false });
        node.addEventListener("touchend", handleTouchEnd, { passive: true });

        // Try auto-fit after mount
        requestAnimationFrame(() => autoFit());
      }
    },
    [applyTransform, syncState, autoFit],
  );

  // Retry auto-fit when viewBox becomes available
  useEffect(() => {
    if (viewBox && !hasAutoFit.current) {
      autoFit();
    }
  }, [viewBox, autoFit]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    dragging.current = true;
    didDrag.current = false;
    lastPos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragging.current) return;
      const dx = e.clientX - lastPos.current.x;
      const dy = e.clientY - lastPos.current.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didDrag.current = true;
      lastPos.current = { x: e.clientX, y: e.clientY };
      live.current.tx += dx;
      live.current.ty += dy;
      applyTransform();
    },
    [applyTransform],
  );

  const onMouseUp = useCallback(() => {
    if (dragging.current) {
      dragging.current = false;
      syncState();
    }
  }, [syncState]);

  const screenToSvg = useCallback(
    (clientX: number, clientY: number): [number, number] => {
      const container = containerRef.current;
      if (!container) return [0, 0];
      const rect = container.getBoundingClientRect();
      const s = live.current;
      const x = (clientX - rect.left - s.tx) / s.scale + vbOffsetX;
      const y = (clientY - rect.top - s.ty) / s.scale + vbOffsetY;
      return [x, y];
    },
    [vbOffsetX, vbOffsetY],
  );

  const zoomToRect = useCallback(
    (minX: number, minY: number, maxX: number, maxY: number, padding = 40) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const boxW = maxX - minX + padding * 2;
      const boxH = maxY - minY + padding * 2;
      const scaleX = rect.width / boxW;
      const scaleY = rect.height / boxH;
      const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.min(scaleX, scaleY)));
      const cx = (minX + maxX) / 2;
      const cy = (minY + maxY) / 2;
      live.current = {
        scale: newScale,
        tx: rect.width / 2 - cx * newScale,
        ty: rect.height / 2 - cy * newScale,
      };
      applyTransform();
      syncState();
    },
    [applyTransform, syncState],
  );

  const resetZoom = useCallback(() => {
    hasAutoFit.current = false;
    autoFit();
  }, [autoFit]);

  /** Restore view from SVG center coords + zoom */
  const setView = useCallback(
    (svgCenterX: number, svgCenterY: number, zoom: number) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, zoom));
      live.current = {
        scale,
        tx: rect.width / 2 - svgCenterX * scale,
        ty: rect.height / 2 - svgCenterY * scale,
      };
      applyTransform();
      syncState();
      hasAutoFit.current = true; // Prevent auto-fit from overriding
    },
    [applyTransform, syncState],
  );

  return {
    containerRef: setContainerRef,
    transform,
    /** Current zoom factor (1 = native viewBox size). Used by overlays
     *  that scale screen-space cluster radii inversely with zoom. */
    scale: state.scale,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    screenToSvg,
    zoomToRect,
    resetZoom,
    setView,
    /** True if the last mousedown-mouseup involved dragging (suppress click) */
    wasDragging: didDrag,
  };
}
